// Empresas emisoras: las panaderias del grupo cuyos datos salen en el remito.
// Alta, edicion y baja (logica). Solo la ve el admin.

import { api } from "../api";
import { iconoLapiz, iconoMas, iconoTacho } from "../iconos";
import type { Empresa } from "../../lib/types";

function esc(s: unknown): string {
  return String(s ?? "").replace(/[&<>"]/g, (c) =>
    ({ "&": "&amp;", "<": "&lt;", ">": "&gt;", '"': "&quot;" })[c] as string,
  );
}

export async function renderEmpresas(contenedor: HTMLElement): Promise<void> {
  let todas: Empresa[] = [];

  contenedor.innerHTML = `
    <div class="mb-4">
      <h1 class="titulo-pagina">Empresas emisoras</h1>
      <p class="subtitulo">Los datos que salen impresos en el remito. Al tomar un pedido se elige cual emite.</p>
    </div>

    <details class="tarjeta mb-4">
      <summary class="flex cursor-pointer list-none items-center gap-2 text-sm font-bold uppercase tracking-wide text-masa-600">
        <span class="text-masa-500">${iconoMas}</span> Nueva empresa
      </summary>
      <form id="f" class="mt-4 grid grid-cols-1 gap-3 sm:grid-cols-2">
        <label class="sm:col-span-2"><span class="etiqueta">Nombre</span><input id="nombre" class="campo" placeholder="Panaderia ..." /></label>
        <label><span class="etiqueta">Direccion</span><input id="direccion" class="campo" /></label>
        <label><span class="etiqueta">Ciudad</span><input id="ciudad" class="campo" placeholder="Montevideo" /></label>
        <label><span class="etiqueta">Telefono</span><input id="telefono" class="campo" /></label>
        <label><span class="etiqueta">Email</span><input id="email" class="campo" /></label>
        <label><span class="etiqueta">Web</span><input id="web" class="campo" /></label>
        <label><span class="etiqueta">Notas</span><input id="notas" class="campo" /></label>
        <div class="flex items-center gap-2 sm:col-span-2">
          <button class="btn-primario">${iconoMas} Agregar</button>
          <span id="err" class="text-sm text-red-600"></span>
        </div>
      </form>
    </details>

    <div class="tarjeta overflow-x-auto p-0">
      <table class="tabla">
        <thead><tr>
          <th>Nombre</th><th>Direccion</th><th>Ciudad</th><th>Telefono</th><th>Estado</th><th class="text-right">Acciones</th>
        </tr></thead>
        <tbody id="tbody"></tbody>
      </table>
      <div id="pie" class="border-t border-crema-200 px-3 py-2 text-sm text-masa-500"></div>
    </div>

    <dialog id="dlg"></dialog>`;

  const tbody = contenedor.querySelector("#tbody") as HTMLElement;
  const pie = contenedor.querySelector("#pie") as HTMLElement;
  const err = contenedor.querySelector("#err") as HTMLElement;
  const dlg = contenedor.querySelector("#dlg") as HTMLDialogElement;

  function pintar(): void {
    if (todas.length === 0) {
      tbody.innerHTML = `<tr><td colspan="6" class="py-6 text-center text-masa-400">Todavia no hay empresas. Sin al menos una activa no se puede confirmar un pedido.</td></tr>`;
      pie.textContent = "";
      return;
    }
    tbody.innerHTML = todas
      .map((e) => {
        const baja = e.activo === 0;
        return `<tr class="${baja ? "text-masa-400" : ""}">
          <td class="${baja ? "line-through" : "font-medium"}">${esc(e.nombre)}</td>
          <td>${esc(e.direccion ?? "")}</td>
          <td>${esc(e.ciudad ?? "")}</td>
          <td>${esc(e.telefono ?? "")}</td>
          <td>${
            baja
              ? `<span class="rounded-md bg-red-50 px-2 py-0.5 text-[11px] font-semibold text-red-700">INACTIVA</span>`
              : `<span class="rounded-md bg-green-50 px-2 py-0.5 text-[11px] font-semibold text-green-700">ACTIVA</span>`
          }</td>
          <td class="whitespace-nowrap text-right">
            <button class="chip-accion" data-editar="${e.id}" title="Editar">${iconoLapiz}</button>
            ${baja ? "" : `<button class="chip-accion" data-baja="${e.id}" title="Desactivar">${iconoTacho}</button>`}
          </td>
        </tr>`;
      })
      .join("");
    const activas = todas.filter((e) => e.activo !== 0).length;
    pie.textContent = `${todas.length} empresa${todas.length === 1 ? "" : "s"} · ${activas} activa${activas === 1 ? "" : "s"}`;
  }

  async function cargar(): Promise<void> {
    tbody.innerHTML = `<tr><td colspan="6" class="py-6 text-center text-masa-400">Cargando...</td></tr>`;
    try {
      todas = await api.get<Empresa[]>("/api/empresas");
      pintar();
    } catch (e) {
      tbody.innerHTML = `<tr><td colspan="6" class="py-6 text-center text-red-700">${esc(e instanceof Error ? e.message : "Error")}</td></tr>`;
    }
  }

  const val = (id: string): string =>
    (contenedor.querySelector(`#${id}`) as HTMLInputElement).value.trim();

  const form = contenedor.querySelector("#f") as HTMLFormElement;
  form.addEventListener("submit", async (ev) => {
    ev.preventDefault();
    err.textContent = "";
    if (!val("nombre")) {
      err.textContent = "El nombre es obligatorio";
      return;
    }
    try {
      await api.post("/api/empresas", {
        nombre: val("nombre"),
        direccion: val("direccion") || null,
        ciudad: val("ciudad") || null,
        telefono: val("telefono") || null,
        email: val("email") || null,
        web: val("web") || null,
        notas: val("notas") || null,
      });
      form.reset();
      (contenedor.querySelector("details") as HTMLDetailsElement).open = false;
      await cargar();
    } catch (e) {
      err.textContent = e instanceof Error ? e.message : "No se pudo agregar";
    }
  });

  function abrirEdicion(e: Empresa): void {
    dlg.innerHTML = `
      <div class="w-[30rem] max-w-full p-5">
        <h2 class="mb-3 text-lg font-bold text-masa-900">Editar empresa</h2>
        <div class="grid grid-cols-1 gap-3 sm:grid-cols-2">
          <label class="sm:col-span-2"><span class="etiqueta">Nombre</span><input id="e_nombre" class="campo" value="${esc(e.nombre)}" /></label>
          <label><span class="etiqueta">Direccion</span><input id="e_direccion" class="campo" value="${esc(e.direccion ?? "")}" /></label>
          <label><span class="etiqueta">Ciudad</span><input id="e_ciudad" class="campo" value="${esc(e.ciudad ?? "")}" /></label>
          <label><span class="etiqueta">Telefono</span><input id="e_telefono" class="campo" value="${esc(e.telefono ?? "")}" /></label>
          <label><span class="etiqueta">Email</span><input id="e_email" class="campo" value="${esc(e.email ?? "")}" /></label>
          <label><span class="etiqueta">Web</span><input id="e_web" class="campo" value="${esc(e.web ?? "")}" /></label>
          <label><span class="etiqueta">Notas</span><input id="e_notas" class="campo" value="${esc(e.notas ?? "")}" /></label>
          <label class="flex items-center gap-2 sm:col-span-2">
            <input id="e_activo" type="checkbox" class="h-4 w-4" ${e.activo === 0 ? "" : "checked"} />
            <span class="text-sm text-masa-700">Activa (aparece al elegir la empresa del pedido)</span>
          </label>
        </div>
        <p id="e_err" class="mt-2 text-sm text-red-600"></p>
        <div class="mt-4 flex justify-end gap-2">
          <button id="e_cancelar" class="btn-sec">Cancelar</button>
          <button id="e_guardar" class="btn-primario">Guardar</button>
        </div>
      </div>`;
    dlg.showModal();
    const eErr = dlg.querySelector("#e_err") as HTMLElement;
    const g = dlg.querySelector("#e_guardar") as HTMLButtonElement;
    const dv = (id: string): string =>
      (dlg.querySelector(`#${id}`) as HTMLInputElement).value.trim();

    dlg.querySelector("#e_cancelar")?.addEventListener("click", () => dlg.close());
    g.addEventListener("click", async () => {
      eErr.textContent = "";
      if (!dv("e_nombre")) {
        eErr.textContent = "El nombre es obligatorio";
        return;
      }
      g.disabled = true;
      try {
        await api.put(`/api/empresas/${e.id}`, {
          nombre: dv("e_nombre"),
          direccion: dv("e_direccion") || null,
          ciudad: dv("e_ciudad") || null,
          telefono: dv("e_telefono") || null,
          email: dv("e_email") || null,
          web: dv("e_web") || null,
          notas: dv("e_notas") || null,
          activo: (dlg.querySelector("#e_activo") as HTMLInputElement).checked,
        });
        dlg.close();
        await cargar();
      } catch (err2) {
        eErr.textContent = err2 instanceof Error ? err2.message : "No se pudo guardar";
        g.disabled = false;
      }
    });
  }

  function confirmarBaja(e: Empresa): void {
    dlg.innerHTML = `
      <div class="w-[26rem] max-w-full p-5">
        <h2 class="mb-1 text-lg font-bold text-masa-900">Desactivar ${esc(e.nombre)}?</h2>
        <p class="mb-4 text-sm text-masa-500">
          Deja de aparecer al elegir la empresa de un pedido. Los pedidos y remitos que ya la usan
          no se tocan: por eso no se borra de verdad. Se puede volver a activar cuando quieras.
        </p>
        <p id="b_err" class="mb-2 text-sm text-red-600"></p>
        <div class="flex justify-end gap-2">
          <button id="b_cancelar" class="btn-sec">Cancelar</button>
          <button id="b_ok" class="btn-peligro">${iconoTacho} Desactivar</button>
        </div>
      </div>`;
    dlg.showModal();
    const bErr = dlg.querySelector("#b_err") as HTMLElement;
    const ok = dlg.querySelector("#b_ok") as HTMLButtonElement;
    dlg.querySelector("#b_cancelar")?.addEventListener("click", () => dlg.close());
    ok.addEventListener("click", async () => {
      bErr.textContent = "";
      ok.disabled = true;
      try {
        await api.del(`/api/empresas/${e.id}`);
        dlg.close();
        await cargar();
      } catch (err2) {
        bErr.textContent = err2 instanceof Error ? err2.message : "No se pudo desactivar";
        ok.disabled = false;
      }
    });
  }

  tbody.addEventListener("click", (ev) => {
    const t = ev.target as HTMLElement;
    const ed = t.closest("[data-editar]") as HTMLElement | null;
    if (ed) {
      const e = todas.find((x) => x.id === Number(ed.dataset.editar));
      if (e) abrirEdicion(e);
      return;
    }
    const ba = t.closest("[data-baja]") as HTMLElement | null;
    if (ba) {
      const e = todas.find((x) => x.id === Number(ba.dataset.baja));
      if (e) confirmarBaja(e);
    }
  });

  await cargar();
}
